import styled from "@emotion/styled";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { entrySearchResultService, getEntryUrl, stripTags } from "@kaltura/mediaspace-shared-utils";
import { prepareItems, TypedSearchResultDataValue } from "./utils";
import { getComponent } from "./component-factory";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { Truncate } from "@kaltura/mediaspace-shared-ui";

export interface SearchResultsProps {
    /**
     * data to show, API search result
     */
    searchResult: KalturaESearchEntryResult;

    /**
     * make it styled-abled
     */
    className?: string;

    /**
     * display detailed search results or basic media info
     */
    showSearchResults?: boolean;
}

const StyledSearchResults = styled("div")(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
}));

const StyledDescription = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

/**
 * list of reasons for this search result
 */
export function SearchResults({ searchResult, className = "", showSearchResults }: SearchResultsProps) {
    // base media page url:
    const config = useConfig();
    const enableEntryTitles = config?.application?.enableEntryTitles;
    const entryName = enableEntryTitles ? searchResult.object.name : undefined;
    const baseMediaPageUrl = getEntryUrl(searchResult.object.id, undefined, entryName);

    // get the list of items to be displayed, ordered by start-time, then non-timed, then tags:
    const searchResultsItems = entrySearchResultService.groupItems(searchResult);
    const items = prepareItems(searchResultsItems);

    // get matching components:
    const components = items.map((item, index) => getComponent(item, `xsr_${index}`, baseMediaPageUrl, className));
    let tagsComponent = null;
    if (!showSearchResults) {
        const tags = searchResult.object.tags;
        // tags may be an empty string and empty string split results in a not-empty array
        // so in case of an empty string, tagsArr is assigned an empty array
        const tagsArr = tags.trim().length === 0 ? [] : tags.split(",").map((tag) => tag.trim());
        const tagsItem: TypedSearchResultDataValue = {
            type: "tags",
            text: "",
            items: tagsArr.map((tag) => ({ text: tag })),
        };
        tagsComponent = getComponent(tagsItem, `xsr_tags`, baseMediaPageUrl, className);
    }

    return (
        <StyledSearchResults className={className}>
            {!showSearchResults && (
                <>
                    <StyledDescription>
                        <Truncate lines={5} showMore={false}>
                            {stripTags(searchResult.object.description)}
                        </Truncate>
                    </StyledDescription>
                    {tagsComponent}
                </>
            )}
            {showSearchResults && components}
        </StyledSearchResults>
    );
}

export default SearchResults;
